import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useUserDetail } from "../../hooks/useApi";
import {
  ArrowLeft,
  Heart,
  Zap,
  Utensils,
  Activity,
  Footprints,
  Moon,
  Droplets,
  Thermometer,
  Wind,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Badge } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/badge";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/tabs";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  Area,
} from "recharts";

const spikeStyles: Record<string, string> = {
  Low: "bg-emerald-500/10 text-emerald-600",
  Moderate: "bg-amber-500/10 text-amber-600",
  High: "bg-destructive/10 text-destructive",
};

const statusStyles: Record<string, string> = {
  active: "bg-emerald-500/10 text-emerald-600",
  inactive: "bg-muted text-muted-foreground",
  suspended: "bg-destructive/10 text-destructive",
};

const getBBSColor = (bbs: number) =>
  bbs >= 80
    ? "text-emerald-600"
    : bbs >= 60
      ? "text-primary"
      : bbs >= 40
        ? "text-amber-500"
        : "text-destructive";

const getMetricColor = (metric: string, value: number) => {
  switch (metric) {
    case "carbFiber":
      return value <= 7
        ? "text-emerald-600"
        : value <= 12
          ? "text-primary"
          : value <= 20
            ? "text-amber-500"
            : "text-destructive";
    case "proteinFiber":
      return value <= 2.5
        ? "text-emerald-600"
        : value <= 4.0
          ? "text-primary"
          : value <= 6.0
            ? "text-amber-500"
            : "text-destructive";
    case "sugarLoad":
      return value <= 0.1
        ? "text-emerald-600"
        : value <= 0.2
          ? "text-amber-500"
          : "text-destructive";
    case "fatQuality":
      return value <= 0.15
        ? "text-emerald-600"
        : value <= 0.3
          ? "text-amber-500"
          : "text-destructive";
    default:
      return "text-foreground";
  }
};

interface AdminUserDetailProps {
  userId?: string;
  dateParam?: string;
  isModal?: boolean;
}

const AdminUserDetail = ({ userId: propUserId, dateParam: propDateParam, isModal = false }: AdminUserDetailProps = {}) => {
  const { id: paramId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Use props if provided (modal mode), otherwise use params (page mode)
  const id = propUserId || paramId;
  const dateParam = propDateParam || searchParams.get('date') || undefined;

  const { data: userDetailData, isLoading, error } = useUserDetail(id || "", dateParam);

  const user = userDetailData?.user;
  const mealLog = userDetailData?.meals_tab?.meals || [];
  const bbsHistory = (userDetailData?.bbs_tab?.history || []).map(h => ({ 
    date: h.month, 
    bbs: h.score ?? null 
  })).filter(h => h.bbs !== null);
  const mealSpikeHistory = (userDetailData?.glucose_tab?.weekly_trend || []).map(t => ({
    day: t.day,
    breakfast: t.breakfast,
    lunch: t.lunch,
    dinner: t.dinner,
    all_meals: t.all_meals
  }));
  const mealAverages = userDetailData?.glucose_tab?.meal_averages || null;
  const healthMetrics = userDetailData?.bbs_tab?.health_ratios ?? {
    carb_fiber_ratio: null,
    protein_fiber_ratio: null,
    sugar_load: null,
    fat_quality: null,
  };
  const fitnessData = {
    heartRate: [],
    steps: (userDetailData?.fitness_tab?.steps_weekly?.graph || []).map(w => ({
      day: w.day,
      steps: w.value
    })),
    sleep: (userDetailData?.fitness_tab?.sleep_weekly?.graph || []).map(s => ({
      day: s.day,
      hours: s.value
    })),
    summary: userDetailData?.fitness_tab?.summary || null,
  };
  const healthInsightsTrend = (userDetailData?.bbs_tab?.health_trend || []).map(t => ({
    month: t.month,
    bbs: t.bbs,
    carbFiber: t.carb_fiber_ratio,
    proteinFiber: t.protein_fiber_ratio,
    sugarLoad: t.sugar_load,
    fatQuality: t.fat_quality,
  })).filter(t => t.bbs !== null);

  const tooltipStyle = {
    backgroundColor: "hsl(var(--card))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "8px",
    color: "hsl(var(--foreground))",
  };

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <p className="text-muted-foreground">Loading user details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-semibold text-destructive">Error loading user details</p>
          <p className="mt-2 text-sm text-muted-foreground">
            {error instanceof Error ? error.message : "An unexpected error occurred"}
          </p>
          <Button
            variant="outline"
            onClick={() => navigate("/admin/users")}
            className="mt-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Users
          </Button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-semibold text-foreground">User not found</p>
          <Button
            variant="outline"
            onClick={() => navigate("/admin/users")}
            className="mt-4"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Users
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={isModal ? "space-y-5 p-6" : "space-y-6"}>
      {/* Header */}
      <div className="flex items-center gap-4">
        {!isModal && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin/users")}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <div className="flex flex-1 items-center gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/10 text-lg font-bold text-primary">
            {user.name
              .split(" ")
              .map(n => n[0])
              .join("")}
          </div>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-foreground">{user.name}</h1>
              <Badge
                variant="secondary"
                className={`${statusStyles[user.status]} border-0 text-xs capitalize`}
              >
                {user.status}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {user.email} · {user.age}y · {user.gender} · Joined{" "}
              {new Date(user.joined).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
              {dateParam && (
                <>
                  {" · "}
                  <span className="font-medium text-primary">
                    Data for: {new Date(dateParam).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="mb-1 text-xs text-muted-foreground">
              Bite Balance Score
            </p>
            <p className={`text-3xl font-bold ${getBBSColor(user.bbs || 0)}`}>
              {user.bbs || "—"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="mb-1 text-xs text-muted-foreground">Glucose Spike</p>
            <Badge
              variant="secondary"
              className={`${
                user.glucose_spike_category 
                  ? spikeStyles[user.glucose_spike_category] || "bg-muted text-muted-foreground"
                  : "bg-muted text-muted-foreground"
              } mt-1 border-0 text-sm`}
            >
              {user.glucose_spike_category || "—"}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="mb-1 text-xs text-muted-foreground">Fitbit</p>
            <Badge
              variant="secondary"
              className={`mt-1 border-0 text-sm ${user.fitbit_connected ? "bg-emerald-500/10 text-emerald-600" : "bg-muted text-muted-foreground"}`}
            >
              {user.fitbit_connected ? "Connected" : "Not Connected"}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="mb-1 text-xs text-muted-foreground">Meals Logged</p>
            <p className="text-3xl font-bold text-foreground">{user.meals_logged || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="meals" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="meals" className="gap-1.5 text-xs sm:text-sm">
            <Utensils className="h-3.5 w-3.5" /> Meals
          </TabsTrigger>
          <TabsTrigger value="glucose" className="gap-1.5 text-xs sm:text-sm">
            <Zap className="h-3.5 w-3.5" /> Glucose
          </TabsTrigger>
          <TabsTrigger value="fitness" className="gap-1.5 text-xs sm:text-sm">
            <Activity className="h-3.5 w-3.5" /> Fitness
          </TabsTrigger>
          <TabsTrigger
            value="bitebalance"
            className="gap-1.5 text-xs sm:text-sm"
          >
            <Heart className="h-3.5 w-3.5" /> BBS
          </TabsTrigger>
        </TabsList>

        {/* MEALS TAB */}
        <TabsContent value="meals" className="space-y-4">
          {mealLog.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Utensils className="mx-auto mb-3 h-12 w-12 text-muted-foreground" />
                <p className="text-lg font-semibold text-foreground">
                  No Meals Logged
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  This user hasn't logged any meals yet.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {mealLog.map((meal, index) => (
                <Card key={index}>
                  <CardContent className="p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-foreground">
                          {meal.meal_type}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {meal.meal_name}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {meal.ghi_label && (
                          <Badge
                            variant="secondary"
                            className={`${
                              meal.ghi_label === "Excellent" 
                                ? "bg-emerald-500/10 text-emerald-600"
                                : meal.ghi_label === "Good"
                                ? "bg-primary/10 text-primary"
                                : "bg-amber-500/10 text-amber-600"
                            } border-0 text-xs`}
                          >
                            {meal.ghi_label}
                          </Badge>
                        )}
                        <div
                          className={`text-lg font-bold ${
                            meal.ghi_score >= 80
                              ? "text-emerald-600"
                              : meal.ghi_score >= 60
                              ? "text-primary"
                              : "text-amber-500"
                          }`}
                        >
                          {meal.ghi_score}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-center">
                      <div className="rounded-md bg-muted/50 p-2">
                        <p className="text-xs text-muted-foreground">Calories</p>
                        <p className="text-sm font-semibold text-foreground">
                          {Math.round(meal.calories)}
                        </p>
                      </div>
                      <div className="rounded-md bg-muted/50 p-2">
                        <p className="text-xs text-muted-foreground">Carbs</p>
                        <p className="text-sm font-semibold text-foreground">
                          {meal.carbs.toFixed(1)}g
                        </p>
                      </div>
                      <div className="rounded-md bg-muted/50 p-2">
                        <p className="text-xs text-muted-foreground">Protein</p>
                        <p className="text-sm font-semibold text-foreground">
                          {meal.protein.toFixed(1)}g
                        </p>
                      </div>
                      <div className="rounded-md bg-muted/50 p-2">
                        <p className="text-xs text-muted-foreground">Fat</p>
                        <p className="text-sm font-semibold text-foreground">
                          {meal.fat.toFixed(1)}g
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* GLUCOSE TAB */}
        <TabsContent value="glucose" className="space-y-4">
          {mealSpikeHistory.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Zap className="mx-auto mb-3 h-12 w-12 text-muted-foreground" />
                <p className="text-lg font-semibold text-foreground">
                  No Glucose Data Available
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  No glucose spike data found for the selected date.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Zap className="h-4 w-4 text-amber-500" />
                    Predicted Glucose Spike by Meal (This Week)
                  </CardTitle>
                  <CardDescription>
                    Average predicted spike (mg/dL) per meal across the week
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={mealSpikeHistory} barCategoryGap="25%" barGap={4}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          className="stroke-muted"
                          vertical={false}
                        />
                        <XAxis
                          dataKey="day"
                          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                          unit=" mg/dL"
                          axisLine={false}
                          tickLine={false}
                        />
                        <Tooltip
                          contentStyle={tooltipStyle}
                          cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }}
                          formatter={(v: number | null) => v !== null ? [`${v} mg/dL`, ""] : ["No data", ""]}
                        />
                        <Legend />
                        <Bar dataKey="breakfast" name="Breakfast" fill="#22c55e" radius={[4, 4, 0, 0]} label={{ position: "top", fontSize: 11, fill: "#22c55e", formatter: (v: number | null) => v != null ? `${v}` : "" }} />
                        <Bar dataKey="lunch" name="Lunch" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} label={{ position: "top", fontSize: 11, fill: "hsl(var(--primary))", formatter: (v: number | null) => v != null ? `${v}` : "" }} />
                        <Bar dataKey="dinner" name="Dinner" fill="#f97316" radius={[4, 4, 0, 0]} label={{ position: "top", fontSize: 11, fill: "#f97316", formatter: (v: number | null) => v != null ? `${v}` : "" }} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {mealAverages && (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                  {Object.entries(mealAverages).map(([meal, value]) => (
                    <Card key={meal}>
                      <CardContent className="p-4 text-center">
                        <p className="mb-1 text-xs text-muted-foreground">
                          {meal.charAt(0).toUpperCase() + meal.slice(1)} Avg Spike
                        </p>
                        <p
                          className={`text-3xl font-bold ${
                            value <= 40
                              ? "text-emerald-600"
                              : value <= 65
                                ? "text-amber-500"
                                : "text-destructive"
                          }`}
                        >
                          {value?.toFixed(1) || "—"}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          mg/dL predicted
                        </p>
                        <Badge
                          variant="secondary"
                          className={`${
                            value <= 40
                              ? "bg-emerald-500/10 text-emerald-600"
                              : value <= 65
                                ? "bg-amber-500/10 text-amber-600"
                                : "bg-destructive/10 text-destructive"
                          } mt-2 border-0 text-xs`}
                        >
                          {value <= 40 ? "Low" : value <= 65 ? "Moderate" : "High"}
                        </Badge>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* FITNESS TAB */}
        <TabsContent value="fitness" className="space-y-4">
          {!fitnessData.summary || (fitnessData.summary.steps === null && fitnessData.summary.active_minutes === null) ? (
            <Card>
              <CardContent className="p-8 text-center">
                <Activity className="mx-auto mb-3 h-12 w-12 text-muted-foreground" />
                <p className="text-lg font-semibold text-foreground">
                  No Fitness Data Available
                </p>
                <p className="mt-1 text-sm text-muted-foreground">
                  No fitness data found for the selected period.
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {fitnessData.summary && (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
                  <Card>
                    <CardContent className="p-3 text-center">
                      <Footprints className="mx-auto mb-1 h-4 w-4 text-primary" />
                      <p className="text-[11px] text-muted-foreground">Steps</p>
                      <p className="text-sm font-bold text-foreground">
                        {fitnessData.summary.steps ? fitnessData.summary.steps.toLocaleString() : "—"}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <Activity className="mx-auto mb-1 h-4 w-4 text-primary" />
                      <p className="text-[11px] text-muted-foreground">Active Mins</p>
                      <p className="text-sm font-bold text-foreground">
                        {fitnessData.summary.active_minutes || "—"}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <Heart className="mx-auto mb-1 h-4 w-4 text-primary" />
                      <p className="text-[11px] text-muted-foreground">Resting HR</p>
                      <p className="text-sm font-bold text-foreground">
                        {fitnessData.summary.resting_hr_bpm && fitnessData.summary.resting_hr_bpm > 0 ? `${fitnessData.summary.resting_hr_bpm} bpm` : "—"}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <Moon className="mx-auto mb-1 h-4 w-4 text-primary" />
                      <p className="text-[11px] text-muted-foreground">Sleep</p>
                      <p className="text-sm font-bold text-foreground">
                        {fitnessData.summary.sleep_hours && fitnessData.summary.sleep_hours > 0 ? `${fitnessData.summary.sleep_hours} hrs` : "—"}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <Droplets className="mx-auto mb-1 h-4 w-4 text-primary" />
                      <p className="text-[11px] text-muted-foreground">SpO2</p>
                      <p className="text-sm font-bold text-foreground">
                        {fitnessData.summary.spo2_percent && fitnessData.summary.spo2_percent > 0 ? `${fitnessData.summary.spo2_percent}%` : "—"}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <Thermometer className="mx-auto mb-1 h-4 w-4 text-primary" />
                      <p className="text-[11px] text-muted-foreground">Skin Temp</p>
                      <p className="text-sm font-bold text-foreground">
                        {fitnessData.summary.skin_temp_celsius != null ? `${fitnessData.summary.skin_temp_celsius}°C` : "—"}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-3 text-center">
                      <Wind className="mx-auto mb-1 h-4 w-4 text-primary" />
                      <p className="text-[11px] text-muted-foreground">Breathing</p>
                      <p className="text-sm font-bold text-foreground">
                        {fitnessData.summary.breathing_rate && fitnessData.summary.breathing_rate > 0 ? `${fitnessData.summary.breathing_rate}/min` : "—"}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {fitnessData.sleep.length > 0 && fitnessData.sleep.some(s => s.hours > 0) ? (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                      Sleep Hours This Week
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={fitnessData.sleep}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            className="stroke-muted"
                          />
                          <XAxis
                            dataKey="day"
                            tick={{
                              fill: "hsl(var(--muted-foreground))",
                              fontSize: 11,
                            }}
                          />
                          <YAxis
                            tick={{
                              fill: "hsl(var(--muted-foreground))",
                              fontSize: 11,
                            }}
                            unit=" hrs"
                          />
                          <Tooltip
                            contentStyle={tooltipStyle}
                            formatter={(v: number) => [`${v} hrs`, ""]}
                          />
                          <Bar
                            dataKey="hours"
                            name="Sleep Hours"
                            fill="hsl(var(--primary))"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Moon className="mx-auto mb-3 h-12 w-12 text-muted-foreground" />
                    <p className="text-lg font-semibold text-foreground">
                      No Sleep Data Available
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      No sleep data found for the selected period.
                    </p>
                  </CardContent>
                </Card>
              )}

              {fitnessData.steps.length > 0 && fitnessData.steps.some(s => s.steps > 0) ? (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                      Steps This Week
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={fitnessData.steps}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            className="stroke-muted"
                          />
                          <XAxis
                            dataKey="day"
                            tick={{
                              fill: "hsl(var(--muted-foreground))",
                              fontSize: 11,
                            }}
                          />
                          <YAxis
                            tick={{
                              fill: "hsl(var(--muted-foreground))",
                              fontSize: 11,
                            }}
                          />
                          <Tooltip
                            contentStyle={tooltipStyle}
                            formatter={(v: number) => [v.toLocaleString(), "Steps"]}
                          />
                          <Bar
                            dataKey="steps"
                            name="Steps"
                            fill="hsl(var(--chart-2))"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center">
                    <Footprints className="mx-auto mb-3 h-12 w-12 text-muted-foreground" />
                    <p className="text-lg font-semibold text-foreground">
                      No Steps Data Available
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      No steps data found for the selected period.
                    </p>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* BITE BALANCE TAB */}
        <TabsContent value="bitebalance" className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Card>
              <CardContent className="p-4 text-center">
                <p className="mb-1 text-xs text-muted-foreground">
                  Carb-Fiber Ratio
                </p>
                <p
                  className={`text-2xl font-bold ${getMetricColor("carbFiber", healthMetrics.carb_fiber_ratio || 0)}`}
                >
                  {healthMetrics.carb_fiber_ratio?.toFixed(1) || "—"}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="mb-1 text-xs text-muted-foreground">
                  Protein-Fiber
                </p>
                <p
                  className={`text-2xl font-bold ${getMetricColor("proteinFiber", healthMetrics.protein_fiber_ratio || 0)}`}
                >
                  {healthMetrics.protein_fiber_ratio?.toFixed(1) || "—"}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="mb-1 text-xs text-muted-foreground">Sugar Load</p>
                <p
                  className={`text-2xl font-bold ${getMetricColor("sugarLoad", healthMetrics.sugar_load || 0)}`}
                >
                  {healthMetrics.sugar_load?.toFixed(1) || "—"}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="mb-1 text-xs text-muted-foreground">
                  Fat Quality
                </p>
                <p
                  className={`text-2xl font-bold ${getMetricColor("fatQuality", healthMetrics.fat_quality || 0)}`}
                >
                  {healthMetrics.fat_quality?.toFixed(1) || "—"}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {bbsHistory.length > 0 ? (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">BBS History</CardTitle>
                  <CardDescription>Score progression over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={bbsHistory}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          className="stroke-muted"
                        />
                        <XAxis
                          dataKey="date"
                          tick={{
                            fill: "hsl(var(--muted-foreground))",
                            fontSize: 11,
                          }}
                        />
                        <YAxis
                          tick={{
                            fill: "hsl(var(--muted-foreground))",
                            fontSize: 11,
                          }}
                          domain={[0, 100]}
                        />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Line
                          type="monotone"
                          dataKey="bbs"
                          name="BBS"
                          stroke="hsl(var(--primary))"
                          strokeWidth={2}
                          dot={{ fill: "hsl(var(--primary))" }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <Heart className="mx-auto mb-3 h-12 w-12 text-muted-foreground" />
                  <p className="text-lg font-semibold text-foreground">
                    No BBS History Available
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    No BBS score history found yet.
                  </p>
                </CardContent>
              </Card>
            )}

            {healthInsightsTrend.length > 0 ? (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">
                    Health Insights Trend
                  </CardTitle>
                  <CardDescription>Ratios improving over time</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={healthInsightsTrend}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          className="stroke-muted"
                        />
                        <XAxis
                          dataKey="month"
                          tick={{
                            fill: "hsl(var(--muted-foreground))",
                            fontSize: 11,
                          }}
                        />
                        <YAxis
                          tick={{
                            fill: "hsl(var(--muted-foreground))",
                            fontSize: 11,
                          }}
                        />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="carbFiber"
                          name="Carb-Fiber"
                          stroke="hsl(var(--primary))"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="proteinFiber"
                          name="Protein-Fiber"
                          stroke="hsl(var(--chart-3))"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="sugarLoad"
                          name="Sugar Load"
                          stroke="hsl(var(--chart-4))"
                          strokeWidth={2}
                        />
                        <Line
                          type="monotone"
                          dataKey="fatQuality"
                          name="Fat Quality"
                          stroke="hsl(var(--chart-2))"
                          strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-8 text-center">
                  <Activity className="mx-auto mb-3 h-12 w-12 text-muted-foreground" />
                  <p className="text-lg font-semibold text-foreground">
                    No Health Trend Data Available
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    No health insights trend data found yet.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminUserDetail;
